import { ApplicationError, IAuthService, ILogger, ITypedStorage, Tenant, TokenData, LocalConfig } from '@velociti/core'
import { TypedStorage } from '@velociti/common'
import axios, { AxiosInstance, AxiosResponse } from 'axios'
import { Store } from 'pinia'
import { deserializeRestApiResponse } from 'src/lib/util/axios-utils'
import { initTheme } from 'src/lib/util/theme-utils'
import { useAuthStore } from 'src/stores/auth'
import { useConfigStore } from 'src/stores/config'
import axiosBearerTokenInterceptor from 'src/system/auth/lib/services/axiosBearerTokenInterceptor'
import ApiResult from 'src/system/auth/models/ApiResult'
import { IAuthStore } from 'src/system/core/model/auth/IAuthStore'

interface IAuthData {
  tokenData: TokenData
  updated: Date
}

export const TENANT_REFRESH_INTERVAL = 1000 * 60 * 5 // 5 minutes

/**
 * Authorisation Service
 *
 * A basic auth services built into the app, designed
 * to connect and authorise with tenant when setting up,
 * using OAuth2
 *
 */
export class AuthService implements IAuthService {
  private auth: Store<'auth', IAuthStore>
  private config: Store<'config', LocalConfig>

  constructor(protected axios: AxiosInstance, protected logger: ILogger) {
    this.auth = useAuthStore()
    this.config = useConfigStore()

    this.enableInterceptors()
  }

  enableInterceptors() {
    this.axios.interceptors.response.use(undefined, axiosBearerTokenInterceptor(this.axios, this))
  }

  async validateTenant(url: string): Promise<string> {
    if (url.endsWith('/')) {
      url = url.slice(0, -1)
    }
    const tenantConfigUrl = `${url}/api/tenant/config`

    try {
      // To bypass the tokem interceptor and be 100% sure, use this instead:
      // const result = await fetch(tenantConfigUrl, {
      //   method: 'GET',
      //   signal: AbortSignal.timeout(5000),
      //   headers: {
      //     'Content-Type': 'application/json',
      //   },
      // })
      // const data = await result.json()

      const response: AxiosResponse = await this.axios.get(tenantConfigUrl, {
        timeout: 5000,
        headers: {
          'Content-Type': 'application/json',
        },
      })
      const data = response.data

      if (typeof data !== 'object' || !data.id) {
        throw new Error(`Invalid tenant: ${url}`)
      }

      return tenantConfigUrl
    } catch (e) {
      console.error('Error validating tenant URL:', e)
      throw new Error(`Invalid tenant: ${url}`)
    }
  }

  getTenant(url?: string): Promise<Tenant> {
    if (url) {
      return this.axios.get(url).then((response) => {
        const tenant = deserializeRestApiResponse<Tenant>(response)
        tenant.lastUpdatedAt = new Date().getTime()
        return tenant
      })
    }
    if (this.config.tenant) {
      return Promise.resolve(this.config.tenant)
    }
    return null
  }

  async switchTenant(tenant: Tenant): Promise<void> {
    this.config.tenant = tenant
    this.axios.defaults.baseURL = tenant.url
    initTheme(tenant)
  }

  async refreshTenantConfig(force = false): Promise<Tenant> {
    const currentConfig = this.config
    if (!currentConfig.tenant || !currentConfig.tenantConfigUrl) {
      console.log('AuthService.refreshTenantConfig: No tenant configuration found in storage')
      return Promise.reject(new Error('No tenant configuration found'))
    }

    if (
      !force &&
      currentConfig.tenant.lastUpdatedAt &&
      new Date().getTime() - currentConfig.tenant.lastUpdatedAt < TENANT_REFRESH_INTERVAL
    ) {
      console.debug('AuthService.refreshTenantConfig: Tenant configuration is up to date')
      return Promise.resolve(currentConfig.tenant)
    }

    const result = await this.axios.get(currentConfig.tenantConfigUrl)
    const tenant = deserializeRestApiResponse<Tenant>(result)
    tenant.lastUpdatedAt = new Date().getTime()
    this.config.tenant = tenant
    console.log('Tenant configuration refreshed', tenant)
    return tenant
  }

  async login(username: string, password: string): Promise<void> {
    const data = { email: username, password }

    const url = this.config.tenant?.authSettings.urls.auth

    if (!url) {
      throw new Error('No auth url found')
    }

    try {
      const result = await this.axios.post(url, new URLSearchParams(data))

      this.logger.debug('Login result', result)

      if (!result.data) {
        throw new Error('Invalid response from login URL: no data')
      }

      this.auth.userId = result.data.user_id.toString()
      this.auth.tokenType = result.data.token_type
      this.auth.expiresIn = result.data.expires_in
      this.auth.accessToken = result.data.access_token
      this.auth.refreshToken = result.data.refresh_token

      this.axios.defaults.headers.common['Authorization'] = `Bearer ${this.auth.accessToken}`
    } catch (result) {
      this.logger.error('Error logging in: ' + (result?.response?.data?.message ?? 'Unknown error'))
      throw ApplicationError.fromApiErrorResult(result)
    }
  }

  async logout(): Promise<void> {
    this.auth.clear()
  }

  async resetPassword(email: string): Promise<void> {
    const url = this.config.tenant?.authSettings.urls.resetPassword

    if (!url) {
      throw new Error('No reset password URL found')
    }

    try {
      const result = await this.axios.post(url, new URLSearchParams({ email }))
      this.logger.debug('Reset password', result)
    } catch (e) {
      this.logger.error('Error resetting password', e?.response?.data?.message || 'Unknown error')
      throw ApplicationError.fromApiErrorResult(e)
    }
  }

  async refresh(): Promise<ApiResult> {
    const data = {
      refresh_token: this.auth.refreshToken || '',
    }

    const url = this.config.tenant?.authSettings.urls.refresh

    if (!url) {
      throw new Error('No refresh url found')
    }

    let result

    try {
      result = await this.axios.post(url, new URLSearchParams(data))

      if (!result.data) {
        throw new Error('Invalid response from login URL')
      }

      this.auth.tokenType = result.data.token_type
      this.auth.expiresIn = result.data.expires_in
      this.auth.accessToken = result.data.access_token
      this.auth.refreshToken = result.data.refresh_token

      this.axios.defaults.headers.common['Authorization'] = `Bearer ${result.data.access_token}`
    } catch (result) {
      this.logger.error('Error refresing token in: ' + (result?.response?.data?.message ?? 'Unknown error'))
      throw ApplicationError.fromApiErrorResult(result)
    }

    return {
      status: result.status,
      message: 'Refreshed Successfully',
      data: result.data,
    }
  }

  getCurrentUser(): Promise<unknown> {
    throw new Error('Method not implemented.')
  }
}
